import { useState } from 'react'
import {
  Star, MessageSquare, Sparkles, Send, ChevronLeft,
  ChevronRight, Filter, Copy, RefreshCw, Check, Loader2,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useReviews, useGenerateReply, useApplyReply } from '../hooks/useGFO'
import { useAIReviewResponse } from '../hooks/useAIReviewResponse'

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          className={cn(
            n <= rating ? 'text-amber-400 fill-amber-400' : 'text-ink-border',
          )}
        />
      ))}
    </div>
  )
}

function AIStar() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M8 0L9.8 6.2L16 8L9.8 9.8L8 16L6.2 9.8L0 8L6.2 6.2L8 0Z" fill="currentColor" />
    </svg>
  )
}

function AIResponsePanel({
  response,
  isLoading,
  error,
  count,
  onRegenerate,
  onUse,
  onResponseChange,
}: {
  response: string | null
  isLoading: boolean
  error: string | null
  count: number
  onRegenerate: () => void
  onUse: (text: string) => void
  onResponseChange: (text: string) => void
}) {
  const [copied, setCopied] = useState(false)

  if (isLoading) {
    return <div className="animate-pulse bg-paper rounded-lg h-20 w-full mt-3 ml-4" />
  }

  if (error) {
    return (
      <div className="mt-3 ml-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
        {error}
      </div>
    )
  }

  if (!response) return null

  const handleCopy = () => {
    navigator.clipboard.writeText(response)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="mt-3 ml-4 space-y-2 animate-fade-in">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 bg-teal/10 text-teal text-[10px] font-medium px-2 py-0.5 rounded-full">
          <AIStar /> Suggestion IA
        </span>
      </div>
      <textarea
        value={response}
        onChange={(e) => onResponseChange(e.target.value)}
        rows={4}
        className="w-full bg-white border border-ink-border rounded-lg p-3 text-sm text-ink resize-none focus:outline-none focus:ring-1 focus:ring-teal"
      />
      <div className="flex items-center gap-2">
        <button onClick={handleCopy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-ink-muted bg-paper-warm hover:bg-paper rounded-lg transition-colors">
          {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
          {copied ? 'Copié' : 'Copier'}
        </button>
        <button onClick={onRegenerate} disabled={count >= 3}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-ink-muted bg-paper-warm hover:bg-paper rounded-lg transition-colors disabled:opacity-40">
          <RefreshCw size={12} /> Régénérer ({count}/3)
        </button>
        <button onClick={() => onUse(response)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-teal hover:bg-teal/90 rounded-lg transition-colors ml-auto">
          <Send size={12} /> Utiliser
        </button>
      </div>
    </div>
  )
}

export function ReviewsPage() {
  const [page, setPage] = useState(1)
  const [ratingFilter, setRatingFilter] = useState<number | undefined>()
  const [responseFilter, setResponseFilter] = useState<boolean | undefined>()
  const [editingReply, setEditingReply] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [reviewIdWithAI, setReviewIdWithAI] = useState<string | null>(null)

  const { data, isLoading } = useReviews(page, {
    rating: ratingFilter,
    needs_response: responseFilter,
  })
  const reviews = data?.reviews || []
  const meta = data?.meta

  const generateReply = useGenerateReply()
  const applyReply = useApplyReply()
  const ai = useAIReviewResponse()

  const handleGenerate = async (reviewId: string) => {
    const result = await generateReply.mutateAsync(reviewId)
    setReplyText(result.ai_reply_draft || '')
    setEditingReply(reviewId)
  }

  const handleSendReply = async (reviewId: string) => {
    await applyReply.mutateAsync({ reviewId, replyText })
    setEditingReply(null)
    setReplyText('')
  }

  const handleAISuggest = (review: any) => {
    if (reviewIdWithAI === review.id) {
      setReviewIdWithAI(null)
      ai.reset()
      return
    }
    ai.reset()
    setReviewIdWithAI(review.id)
    ai.suggest({
      review_text: review.review_text || '',
      rating: review.rating,
      business_name: 'Mon entreprise',
      business_type: 'entreprise locale',
    })
  }

  const handleUseAI = (text: string) => {
    setReplyText(text)
    setEditingReply(reviewIdWithAI)
    setReviewIdWithAI(null)
    ai.reset()
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Avis Google</h1>
        <p className="text-sm text-ink-muted mt-1">
          Gérez vos avis clients avec l'aide de l'IA
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-ink-faint" />
        <button
          onClick={() => { setRatingFilter(undefined); setResponseFilter(undefined); setPage(1) }}
          className={cn(
            'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
            !ratingFilter && responseFilter === undefined
              ? 'bg-violet/10 text-violet' : 'text-ink-muted hover:bg-paper-warm',
          )}
        >
          Tous
        </button>
        <button
          onClick={() => { setResponseFilter(true); setRatingFilter(undefined); setPage(1) }}
          className={cn(
            'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
            responseFilter === true ? 'bg-brick-light text-brick' : 'text-ink-muted hover:bg-paper-warm',
          )}
        >
          Sans réponse
        </button>
        {[5, 4, 3, 2, 1].map((r) => (
          <button
            key={r}
            onClick={() => { setRatingFilter(r); setResponseFilter(undefined); setPage(1) }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1',
              ratingFilter === r ? 'bg-amber-50 text-amber-600' : 'text-ink-muted hover:bg-paper-warm',
            )}
          >
            {r} <Star size={10} className="fill-current" />
          </button>
        ))}
      </div>

      {/* Reviews List */}
      <div className="space-y-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-card border border-ink-border bg-white p-5 animate-pulse">
              <div className="flex gap-3">
                <div className="h-10 w-10 rounded-full bg-paper-warm" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 rounded bg-paper-warm" />
                  <div className="h-3 w-full rounded bg-paper-warm" />
                  <div className="h-3 w-3/4 rounded bg-paper-warm" />
                </div>
              </div>
            </div>
          ))
        ) : reviews.length === 0 ? (
          <div className="rounded-card border border-ink-border bg-white p-12 text-center text-sm text-ink-muted">
            Aucun avis trouvé
          </div>
        ) : (
          reviews.map((review: any) => (
            <div
              key={review.id}
              className={cn(
                'rounded-card border bg-white p-5 shadow-card transition-all',
                review.needs_response ? 'border-amber-200' : 'border-ink-border',
              )}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-paper-warm text-sm font-semibold text-ink-muted">
                  {(review.author_name || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-ink">
                      {review.author_name || 'Anonyme'}
                    </span>
                    <StarRating rating={review.rating} />
                    {review.needs_response && (
                      <span className="rounded-lg bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-600">
                        EN ATTENTE
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-ink leading-relaxed">
                    {review.review_text || <span className="italic text-ink-faint">Pas de commentaire</span>}
                  </p>

                  {/* Existing reply */}
                  {review.reply_text && (
                    <div className="ml-4 mt-3 rounded-lg border-l-2 border-violet/30 bg-violet/5 px-4 py-3">
                      <p className="text-xs font-medium text-violet mb-1">Votre réponse</p>
                      <p className="text-sm text-ink">{review.reply_text}</p>
                    </div>
                  )}

                  {/* AI Draft (old system) */}
                  {review.ai_reply_draft && !review.reply_text && editingReply !== review.id && (
                    <div className="ml-4 mt-3 rounded-lg border border-dashed border-violet/30 bg-violet/5 px-4 py-3">
                      <p className="text-xs font-medium text-violet mb-1 flex items-center gap-1">
                        <Sparkles size={12} /> Brouillon IA
                      </p>
                      <p className="text-sm text-ink">{review.ai_reply_draft}</p>
                    </div>
                  )}

                  {/* AI Suggestion Panel (new system) */}
                  {reviewIdWithAI === review.id && (
                    <AIResponsePanel
                      response={ai.response}
                      isLoading={ai.isLoading}
                      error={ai.error}
                      count={ai.count}
                      onRegenerate={ai.regenerate}
                      onUse={handleUseAI}
                      onResponseChange={ai.setResponse}
                    />
                  )}

                  {/* Reply editor */}
                  {editingReply === review.id && (
                    <div className="ml-4 mt-3 space-y-2">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={4}
                        className="w-full rounded-lg border border-ink-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold resize-none"
                        placeholder="Votre réponse..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSendReply(review.id)}
                          disabled={!replyText.trim() || applyReply.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-brick px-3 py-1.5 text-xs font-medium text-white hover:bg-brick-hover disabled:opacity-50 transition-colors"
                        >
                          <Send size={12} /> Publier
                        </button>
                        <button
                          onClick={() => { setEditingReply(null); setReplyText('') }}
                          className="rounded-lg px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-paper-warm transition-colors"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {review.needs_response && !review.reply_text && editingReply !== review.id && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleAISuggest(review)}
                        disabled={ai.isLoading}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                          reviewIdWithAI === review.id
                            ? 'bg-teal text-white'
                            : 'border border-teal/20 bg-teal/10 text-teal hover:bg-teal/15',
                        )}
                      >
                        {ai.isLoading && reviewIdWithAI === review.id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <AIStar />}
                        Suggestion IA
                      </button>
                      <button
                        onClick={() => { setEditingReply(review.id); setReplyText(review.ai_reply_draft || '') }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-ink-border px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-paper-warm transition-colors"
                      >
                        <MessageSquare size={12} /> Répondre
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {meta && meta.total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-ink-faint">{meta.total} avis</p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg p-1.5 hover:bg-paper-warm disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-3 py-1 text-xs font-medium text-ink-muted">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={reviews.length < 20}
              className="rounded-lg p-1.5 hover:bg-paper-warm disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
